import { useState, useEffect } from 'react'
import { getRandomKeywords } from '@creanizer/utils'
import {
  Label,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Checkbox,
} from '@creanizer/ui'
import { Dices, RefreshCw, Globe, Users, Shield, Package, ChevronDown, ChevronUp, Search, Eye, Map } from 'lucide-react'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { EntitySelector } from './EntitySelector'
import type { PlotGenre, PlotStage, PlotKeyword, SavedWorldBuilding, SavedCharacter, SavedEnemy, SavedItem, SavedDungeon, SavedCommunity, SavedSociety } from '@creanizer/types'

const genres: Record<PlotGenre | string, string> = {
  action: 'アクション',
  suspense: 'サスペンス',
  horror: 'ホラー',
  comedy: 'コメディ',
  romance: 'ラブロマンス',
  fantasy: 'ファンタジー',
  sf: 'SF',
  mystery: 'ミステリー',
  slice_of_life: '日常系',
  thriller: 'スリラー',
  adventure: '冒険',
  drama: 'ドラマ',
}

const stages: Record<PlotStage | string, string> = {
  modern_japan: '現代日本',
  isekai_fantasy: '異世界ファンタジー',
  near_future: '近未来',
  sf_space: 'SF宇宙',
  medieval_europe: '中世ヨーロッパ',
  edo_japan: '江戸時代',
  post_apocalypse: 'ポストアポカリプス',
  cyberpunk: 'サイバーパンク',
  steampunk: 'スチームパンク',
  underwater: '海底都市',
  floating_city: '浮遊都市',
  virtual_reality: '仮想現実',
}

interface StandardModeFormProps {
  genreMode: 'select' | 'custom'
  setGenreMode: (mode: 'select' | 'custom') => void
  genre: PlotGenre
  setGenre: (genre: PlotGenre) => void
  genreCustom: string
  setGenreCustom: (genre: string) => void
  stageMode: 'select' | 'custom'
  setStageMode: (mode: 'select' | 'custom') => void
  stage: PlotStage
  setStage: (stage: PlotStage) => void
  stageCustom: string
  setStageCustom: (stage: string) => void
  keywords: PlotKeyword[]
  setKeywords: (keywords: PlotKeyword[]) => void
  keywordModes: ('random' | 'custom')[]
  setKeywordModes: (modes: ('random' | 'custom')[]) => void
  selectedWorldId: string | null
  setSelectedWorldId: (id: string | null) => void
  selectedDungeonId: string | null
  setSelectedDungeonId: (id: string | null) => void
  selectedCharacterIds: string[]
  setSelectedCharacterIds: (ids: string[]) => void
  selectedEnemyIds: string[]
  setSelectedEnemyIds: (ids: string[]) => void
  selectedItemIds: string[]
  setSelectedItemIds: (ids: string[]) => void
  selectedCommunityIds: string[]
  setSelectedCommunityIds: (ids: string[]) => void
  selectedSocietyIds: string[]
  setSelectedSocietyIds: (ids: string[]) => void
}

export function StandardModeForm({
  genreMode,
  setGenreMode,
  genre,
  setGenre,
  genreCustom,
  setGenreCustom,
  stageMode,
  setStageMode,
  stage,
  setStage,
  stageCustom,
  setStageCustom,
  keywords,
  setKeywords,
  keywordModes,
  setKeywordModes,
  selectedWorldId,
  setSelectedWorldId,
  selectedDungeonId,
  setSelectedDungeonId,
  selectedCharacterIds,
  setSelectedCharacterIds,
  selectedEnemyIds,
  setSelectedEnemyIds,
  selectedItemIds,
  setSelectedItemIds,
  selectedCommunityIds,
  setSelectedCommunityIds,
  selectedSocietyIds,
  setSelectedSocietyIds,
}: StandardModeFormProps) {
  // ストアからデータを取得
  const { savedWorlds } = useWorldBuilderStore()
  const { dungeons } = useDungeonStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const { items } = useItemStore()
  const { savedCommunities } = useCommunityStore()
  const { savedSocieties } = useSocietyStore()
  // キーワードの再抽選
  const rerollKeyword = (index: number) => {
    const newKeyword = getRandomKeywords(1, Date.now() + index)[0]
    const newKeywords = [...keywords]
    newKeywords[index] = {
      value: newKeyword,
      isCustom: false,
      index: Math.floor(Math.random() * 700),
    }
    setKeywords(newKeywords)
  }

  // キーワードモードの切り替え
  const toggleKeywordMode = (index: number) => {
    const newModes = [...keywordModes]
    newModes[index] = newModes[index] === 'random' ? 'custom' : 'random'
    setKeywordModes(newModes)
    
    if (newModes[index] === 'random') {
      rerollKeyword(index)
    } else {
      const newKeywords = [...keywords]
      newKeywords[index] = {
        value: '',
        isCustom: true,
      }
      setKeywords(newKeywords)
    }
  }

  // カスタムキーワードの更新
  const updateCustomKeyword = (index: number, value: string) => {
    const newKeywords = [...keywords]
    newKeywords[index] = {
      value,
      isCustom: true,
    }
    setKeywords(newKeywords)
  }

  return (
    <>
      {/* 世界観選択 */}
      {savedWorlds.length > 0 && (
        <div className="space-y-2">
          <Label>世界観を選択（任意）</Label>
          <Select
            value={selectedWorldId || 'none'}
            onValueChange={(value) => setSelectedWorldId(value === 'none' ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択...">
                {selectedWorldId ? (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                  </div>
                ) : (
                  '選択しない'
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <div className="flex items-center gap-2">
                  選択しない
                </div>
              </SelectItem>
              {savedWorlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  <div className="flex flex-col">
                    <div className="font-medium">{world.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {world.result.summary?.substring(0, 50)}...
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedWorldId && (
            <div className="p-2 bg-blue-50 rounded text-xs">
              <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
              <div className="text-blue-700">
                {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
              </div>
              <div className="text-orange-600 mt-1">
                ※世界観を選択した場合、ジャンルと舞台は世界観の設定が優先されます
              </div>
            </div>
          )}
        </div>
      )}

      {/* ダンジョン選択 */}
      {dungeons.length > 0 && (
        <div className="space-y-2">
          <Label>ダンジョンを選択（任意）</Label>
          <Select
            value={selectedDungeonId || 'none'}
            onValueChange={(value) => setSelectedDungeonId(value === 'none' ? null : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="ダンジョンを選択...">
                {selectedDungeonId ? (
                  <div className="flex items-center gap-2">
                    <Map className="h-4 w-4" />
                    {dungeons.find(d => d.id === selectedDungeonId)?.title}
                  </div>
                ) : (
                  '選択しない'
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <div className="flex items-center gap-2">
                  選択しない
                </div>
              </SelectItem>
              {dungeons.map((dungeon) => (
                <SelectItem key={dungeon.id} value={dungeon.id}>
                  <div className="flex flex-col">
                    <div className="font-medium">{dungeon.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {dungeon.result.basicInfo.description?.substring(0, 50)}...
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDungeonId && (
            <div className="p-2 bg-purple-50 rounded text-xs">
              <div className="font-medium text-purple-900 mb-1">選択中のダンジョン</div>
              <div className="text-purple-700">
                {dungeons.find(d => d.id === selectedDungeonId)?.result.basicInfo.description?.substring(0, 100)}...
              </div>
              <div className="text-orange-600 mt-1">
                ※ダンジョンを選択した場合、ダンジョン探索のプロットが生成されます
              </div>
            </div>
          )}
        </div>
      )}

      {/* ジャンル選択（世界観未選択時のみ表示） */}
      {!selectedWorldId && (
        <div className="space-y-2">
        <Label>ジャンル</Label>
        <Tabs value={genreMode} onValueChange={(v) => setGenreMode(v as 'select' | 'custom')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="select">選択</TabsTrigger>
            <TabsTrigger value="custom">自由入力</TabsTrigger>
          </TabsList>
          <TabsContent value="select" className="mt-2">
            <Select value={genre} onValueChange={(v) => setGenre(v as PlotGenre)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(genres).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="custom" className="mt-2">
            <Input
              placeholder="ジャンルを入力..."
              value={genreCustom}
              onChange={(e) => setGenreCustom(e.target.value)}
            />
          </TabsContent>
        </Tabs>
      </div>
      )}

      {/* 舞台選択（世界観未選択時のみ表示） */}
      {!selectedWorldId && (
      <div className="space-y-2">
        <Label>舞台</Label>
        <Tabs value={stageMode} onValueChange={(v) => setStageMode(v as 'select' | 'custom')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="select">選択</TabsTrigger>
            <TabsTrigger value="custom">自由入力</TabsTrigger>
          </TabsList>
          <TabsContent value="select" className="mt-2">
            <Select value={stage} onValueChange={(v) => setStage(v as PlotStage)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(stages).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="custom" className="mt-2">
            <Input
              placeholder="舞台を入力..."
              value={stageCustom}
              onChange={(e) => setStageCustom(e.target.value)}
            />
          </TabsContent>
        </Tabs>
      </div>
      )}

      {/* キーワード */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>キーワード（3つ）</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const newKeywords = getRandomKeywords(3, Date.now())
              setKeywords(newKeywords.map((value, index) => ({
                value,
                isCustom: false,
                index,
              })))
              setKeywordModes(['random', 'random', 'random'])
            }}
          >
            <Dices className="h-4 w-4 mr-2" />
            すべて再抽選
          </Button>
        </div>
        <div className="space-y-3">
          {keywords.map((keyword, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-4">{index + 1}.</span>
              {keywordModes[index] === 'random' ? (
                <>
                  <div className="flex-1 px-3 py-2 bg-gray-50 rounded-md text-sm">
                    {keyword.value || 'キーワード生成中...'}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => rerollKeyword(index)}
                    title="再抽選"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Input
                  placeholder="キーワードを入力..."
                  value={keyword.value}
                  onChange={(e) => updateCustomKeyword(index, e.target.value)}
                  className="flex-1"
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => toggleKeywordMode(index)}
              >
                {keywordModes[index] === 'random' ? '自由入力' : 'ランダム'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* キャラクター選択 */}
      <EntitySelector
        title="保存されたキャラクターを使用"
        icon="users"
        entities={characters.map(c => ({
          id: c.id,
          name: c.result.name,
          summary: `${c.result.age} / ${c.result.role} / ${c.result.gender}`,
        }))}
        selectedIds={selectedCharacterIds}
        onToggle={(id) => {
          setSelectedCharacterIds(
            selectedCharacterIds.includes(id)
              ? selectedCharacterIds.filter(cId => cId !== id)
              : [...selectedCharacterIds, id]
          )
        }}
        searchPlaceholder="名前、役割、年齢などで検索..."
      />

      {/* エネミー選択 */}
      <EntitySelector
        title="保存されたエネミーを使用"
        icon="shield"
        entities={enemies.map(e => ({
          id: e.id,
          name: e.result.basicInfo.name,
          summary: `${e.result.basicInfo.type} / ${e.result.basicInfo.category} / 脅威度${e.result.basicInfo.threat}`,
        }))}
        selectedIds={selectedEnemyIds}
        onToggle={(id) => {
          setSelectedEnemyIds(
            selectedEnemyIds.includes(id)
              ? selectedEnemyIds.filter(eId => eId !== id)
              : [...selectedEnemyIds, id]
          )
        }}
        searchPlaceholder="名前、種別、カテゴリなどで検索..."
      />

      {/* アイテム選択 */}
      <EntitySelector
        title="保存されたアイテムを使用"
        icon="package"
        entities={items.map(i => ({
          id: i.id,
          name: i.result.basicInfo.name,
          summary: `${i.result.basicInfo.type} / ${i.result.basicInfo.rarity}`,
        }))}
        selectedIds={selectedItemIds}
        onToggle={(id) => {
          setSelectedItemIds(
            selectedItemIds.includes(id)
              ? selectedItemIds.filter(iId => iId !== id)
              : [...selectedItemIds, id]
          )
        }}
        searchPlaceholder="名前、種類、レアリティなどで検索..."
      />

      {/* コミュニティ選択 */}
      <EntitySelector
        title="保存されたコミュニティを使用"
        icon="map"
        entities={savedCommunities.filter(c => c?.result?.basicInfo).map(c => ({
          id: c.id,
          name: c.result.basicInfo.name,
          summary: `${c.result.basicInfo.type} / 人口: ${c.result.basicInfo.population?.toLocaleString()}人`,
        }))}
        selectedIds={selectedCommunityIds}
        onToggle={(id) => {
          setSelectedCommunityIds(
            selectedCommunityIds.includes(id)
              ? selectedCommunityIds.filter(cId => cId !== id)
              : [...selectedCommunityIds, id]
          )
        }}
        searchPlaceholder="名前、タイプ、人口などで検索..."
      />

      {/* ソサエティ選択 */}
      <EntitySelector
        title="保存された組織を使用"
        icon="users"
        entities={savedSocieties.filter(s => s?.result?.basicInfo).map(s => ({
          id: s.id,
          name: s.result.basicInfo.name,
          summary: `${s.result.basicInfo.type} / 規模: ${s.result.basicInfo.scale}`,
        }))}
        selectedIds={selectedSocietyIds}
        onToggle={(id) => {
          setSelectedSocietyIds(
            selectedSocietyIds.includes(id)
              ? selectedSocietyIds.filter(sId => sId !== id)
              : [...selectedSocietyIds, id]
          )
        }}
        searchPlaceholder="名前、タイプ、規模などで検索..."
      />
    </>
  )
}